import { useEffect, useState, type ReactNode } from 'react';

import { CommandPalette } from './CommandPalette';
import { EnvironmentHairline } from './EnvironmentBadge';
import { ImpersonationBanner } from './ImpersonationBanner';
import { MobileNavDrawer } from './MobileNavDrawer';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const SIDEBAR_STORAGE_KEY = 'mesh-admin.sidebar-expanded';

function readSidebarExpanded(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(readSidebarExpanded);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarExpanded));
    } catch {
      // Non-fatal.
    }
  }, [sidebarExpanded]);

  // ⌘K / Ctrl+K opens search from anywhere. Registered on the shell rather than
  // per-screen so it works even while a list is loading.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="bg-bg relative flex min-h-screen flex-col">
      <EnvironmentHairline />

      <a
        href="#mesh-screen"
        className="focus:border-bd-s focus:bg-card focus:text-tx sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-200 focus:rounded-md focus:border focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>

      <TopBar
        onOpenDrawer={() => setDrawerOpen(true)}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <ImpersonationBanner />

      <div className="flex min-h-0 flex-1 items-stretch">
        <Sidebar
          expanded={sidebarExpanded}
          onToggle={() => setSidebarExpanded((expanded) => !expanded)}
        />

        <main className="flex min-w-0 flex-1 flex-col">
          <div id="mesh-screen" className="min-w-0 flex-1 p-3.5 md:p-5">
            {children}
          </div>
        </main>
      </div>

      <MobileNavDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
